/**
 * EventLog — narrow right-hand column rendering `log` as one-liners
 * (ui/31 §2/§3, R14: the log is derived from the event stream, NOT part of
 * `GameState`). Hidden for now (Q4). Colour coding follows 01-foundations §2.3:
 * the viewer has no colour of their own, so their events are plain cream, the
 * opponent's are pink, everything else muted.
 * Text and ownership are derived only from already-public event payloads.
 */
import { memo } from 'react';
import type { CSSProperties, ReactElement } from 'react';
import type { PlayerId, Side } from '@cards/engine';
import type { LogEntry } from '../types.js';
import { COLORS } from './kit/tokens.js';
import { playerDisplayName } from './locale.js';
import { propsEqualByValue } from './memoEquality.js';
import { formatPublicPlaybackEvent } from './publicPlayback.js';

export interface EventLogProps {
  log: readonly LogEntry[];
  /**
   * The only thing this column needs off the snapshot. Taking the id rather than
   * the `GameState` is what lets the memo below hold: the log is derived from the
   * event stream (R14), so a new snapshot with the same log has nothing to say
   * here — and now it cannot, because the snapshot is not in reach.
   */
  matchId: string;
  viewerId: PlayerId;
  /** The viewer's side, so cells read «ваша сторона» / «сторона соперника». */
  viewerSide?: Side;
  /** Public display names keyed by internal player ids. */
  playerNames?: Readonly<Partial<Record<PlayerId, string>>>;
}

interface EventLogEntryProps {
  entry: LogEntry;
  viewerId: PlayerId;
  viewerSide?: Side;
  playerNames?: Readonly<Partial<Record<PlayerId, string>>>;
}

function EventLogEntryRow({ entry, viewerId, viewerSide, playerNames }: EventLogEntryProps): ReactElement {
  const label = formatPublicPlaybackEvent(entry.event, {
    labelPlayer: (playerId: PlayerId | 'draw') => playerDisplayName(playerId, viewerId, playerNames),
    ...(viewerSide !== undefined ? { viewerSide } : {}),
  });
  const owner = label.ownerId;
  const color =
    owner === undefined
      ? COLORS.textMutedOnDark
      : owner === viewerId
        ? COLORS.textOnDark
        : COLORS.textRivalOnDark;
  return (
    <div
      className="cards-event-log-entry"
      data-log-index={entry.index}
      style={{ '--cards-event-color': color } as CSSProperties}
    >
      {label.text}
    </div>
  );
}

/**
 * One line, memoised on its own.
 *
 * A revealed event appends to the log, which is a new array — so the column below
 * re-renders, and it used to re-format the entire history to do it: three hundred
 * turns in, that is three hundred formatter calls to add one line, during playback,
 * on the frames that are already busy animating. An entry that has been shown once
 * never changes (the store appends, it does not rewrite), so its object identity
 * makes the comparison a single `Object.is` and the formatting runs once per line
 * for the life of the match.
 */
const EventLogEntry = memo(EventLogEntryRow, propsEqualByValue);

function EventLogComponent({
  log,
  matchId,
  viewerId,
  viewerSide,
  playerNames,
}: EventLogProps): ReactElement {
  const visibleLog = log;
  return (
    <div
      className="cards-event-log"
      data-testid="event-log"
    >
      <div
        className="cards-event-log-game-id"
        data-testid="event-log-game-id"
      >
        ID игры: {matchId}
      </div>
      {/* Newest first, so the copy stays: a CSS reversal would put the oldest
         entry first in the DOM, and both the game-id header and the playback
         tests read this column in document order. */}
      {[...visibleLog].reverse().map((entry) => (
        <EventLogEntry
          key={entry.index}
          entry={entry}
          viewerId={viewerId}
          {...(viewerSide !== undefined ? { viewerSide } : {})}
          {...(playerNames !== undefined ? { playerNames } : {})}
        />
      ))}
    </div>
  );
}

/**
 * A whole match's history, rebuilt from scratch on every render of its parent —
 * and GameView re-renders for things this column cannot possibly care about: a
 * card picked up, a card released, a targeting step, a flight launching and
 * landing. Formatting and reconciling a few hundred entries is real main-thread
 * work, and on a phone it is worse than useless: the column is `display: none`
 * there, so the cost bought nothing that could be seen.
 *
 * `log` comes off the playback store by identity, so the comparison is normally
 * one `Object.is` (see dataEqual); the by-value fallback is for `playerNames`,
 * which App rebuilds as a fresh object on each of ITS renders.
 */
export const EventLog = memo(EventLogComponent, propsEqualByValue);
